import { Box, Tooltip, Typography } from '@mui/material';

import { CuitPresentacionSvcc } from '../hooks/usePresentacionesSVCC';
import VigenciaBadge from './VigenciaBadge';

/**
 * Los plazos de la fila, que son DOS y no se pueden leer como uno solo:
 *
 * - **El de la DDJJ**: la declaración del período AAAA vence el 01/04/AAAA+1.
 *   Es la obligación legal, existe desde que el informe tiene período y no
 *   depende de que se haya empezado ningún trámite.
 * - **La purga del borrador**: la SRT borra las presentaciones sin consolidar a
 *   los 30 días. Sólo existe con trámite abierto y es una estimación nuestra.
 *
 * La columna mostraba únicamente el segundo, así que quedaba vacía justo en las
 * filas que todavía hay que enviar — las únicas donde el plazo importa. Ahora
 * van los dos, cada uno con su etiqueta: sin decir cuál es cuál, dos fechas
 * juntas se leen como la misma cosa.
 */

/** La DDJJ del período AAAA vence el 1 de abril de AAAA+1. */
const vencimientoDdjj = (anioPeriodo: number): Date =>
    new Date(Date.UTC(anioPeriodo + 1, 3, 1));

const formatear = (fecha: Date): string =>
    fecha.toLocaleDateString('es-AR', {
        timeZone: 'UTC',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });

interface PlazosCeldaProps {
    fila: Pick<CuitPresentacionSvcc, 'anioPeriodo' | 'fechaVencimientoEstimada'>;
    /** Ver `VigenciaBadge`: el estado DESINCRONIZADA no tiene borrador que purgar. */
    vigenciaSuprimida?: boolean;
}

const PlazosCelda = ({ fila, vigenciaSuprimida = false }: PlazosCeldaProps) => {
    const anioPeriodo = fila.anioPeriodo ?? null;
    const vence = anioPeriodo === null ? null : vencimientoDdjj(anioPeriodo);
    const vencida = vence !== null && vence.getTime() < Date.now();

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {vence === null ? (
                /*
                 * Sin período no hay vencimiento de DDJJ que calcular. Se dice
                 * por qué falta en vez de dejar la línea muda: lo que hay que
                 * hacer es asignarle el período a la fila.
                 */
                <Typography
                    variant="body2"
                    data-testid="plazo-sin-periodo"
                    sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                >
                    Se calcula al asignar el período
                </Typography>
            ) : (
                <Tooltip
                    title={`La declaración del período ${anioPeriodo} vence el 1 de abril de ${
                        (anioPeriodo as number) + 1
                    }.`}
                >
                    <Typography
                        variant="body2"
                        data-testid="plazo-ddjj"
                        sx={{
                            // Chip como los del estado: rojo vencida, gris en término.
                            display: 'inline-flex',
                            alignItems: 'center',
                            height: 24,
                            px: 1.25,
                            borderRadius: '999px',
                            fontSize: 12,
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            color: vencida ? '#B42318' : '#3D4A50',
                            bgcolor: vencida ? '#FEF3F2' : '#EEF2F3',
                            cursor: 'help',
                            width: 'fit-content'
                        }}
                    >
                        {vencida
                            ? `DDJJ vencida ${formatear(vence)}`
                            : `DDJJ vence ${formatear(vence)}`}
                    </Typography>
                </Tooltip>
            )}
            {/*
              * Va fuera del if a propósito: la purga del borrador no depende
              * del período. Una fila sin período pero con trámite abierto
              * tiene igual sus 30 días corriendo, y esconderlos era peor que
              * no tener la columna.
              */}
            <VigenciaBadge
                fechaVencimientoEstimada={fila.fechaVencimientoEstimada}
                suprimido={vigenciaSuprimida}
            />
        </Box>
    );
};

export default PlazosCelda;
